import { useApiClient } from '~/api-client';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
    EventPanelResource,
    EventPanelOptions,
    ApiResultEventPanelOptions,
    SubdivisionOption,
} from '@mark43/cad-api';
import { useMemo } from 'react';
import {
    CallForServiceOption,
    EventLabelOption,
    AgencyProfileOption,
    DispatchAreaOption,
    AttributeOption,
    ColumnDefinitionEnum,
    ColumnDefinitionEnumType,
} from '@mark43/cad-api';
import { globalAttributes } from '../common/global-attributes';
import {
    getPrimarySubdivisionNameKeyByAgencyType,
    PrimarySubdivisionNames,
} from '../map-window/components/helpers';
import { getPrimarySubdivisionOptions } from '../map-window/components/helpers';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useIsAgencyTypeGlobalAttributeIdConsidered } from '../common/hooks/use-is-agency-type-global-attribute-id-considered';

interface UseEventPanelOptionsReturnType extends Omit<UseQueryResult<EventPanelOptions>, 'data'> {
    eventqueuefilteroptions: EventPanelOptions;
}

// this is created for type safety
export type WithColumnDefinition<Option> = Option & { columnDefinition: ColumnDefinitionEnumType };

export interface EventQueueFilterOptions {
    additionalCfsOptions: WithColumnDefinition<CallForServiceOption>[];
    cfsOptions: WithColumnDefinition<CallForServiceOption>[];
    priorityOptions: WithColumnDefinition<AttributeOption>[];
    alarmLevelOptions: WithColumnDefinition<AttributeOption>[];
    eventLabelOptions: WithColumnDefinition<EventLabelOption>[];
    dispatchAreaOptions: WithColumnDefinition<DispatchAreaOption>[];
    agencyOptions: WithColumnDefinition<AgencyProfileOption>[];
    eventPrimarySubdivisionOptions: WithColumnDefinition<SubdivisionOption>[];
    primarySubdivisionName: string | undefined;
}

export const EVENT_PANEL_OPTIONS_CACHE_KEY = 'event-panel-options';

export function useEventPanelOptions(): UseEventPanelOptionsReturnType {
    const apiClient = useApiClient();

    const { data, ...rest } = useQuery(
        [EVENT_PANEL_OPTIONS_CACHE_KEY],
        () => {
            return apiClient
                .request<EventPanelResource.GetEventPanelOptions>({
                    method: 'GET',
                    url: 'event_panel/options/',
                })
                .then((resp): ApiResultEventPanelOptions => resp.data)
                .then((result) => {
                    return result.data ?? defaultEventPanelOptions;
                });
        },
        {
            refetchOnMount: false,
            cacheTime: Infinity,
            staleTime: Infinity,
        }
    );

    return {
        eventqueuefilteroptions: data ?? defaultEventPanelOptions,
        ...rest,
    };
}

// return empty EventPanelOptions if API call fails/takes too long
const defaultEventPanelOptions: EventPanelOptions = {
    alarmLevelOptions: [],
    callForServiceOptions: [],
    dispositionOptions: [],
    emsAgencyProfileOptions: [],
    emsDispatchAreaOptions: [],
    emsSubdivision1Options: [],
    emsSubdivision2Options: [],
    emsSubdivision3Options: [],
    emsSubdivision4Options: [],
    emsSubdivision5Options: [],
    eventLabelOptions: [],
    eventOriginOptions: [],
    eventOriginSelfInitiateOptions: [],
    eventQueueOptions: [],
    eyeColorOptions: [],
    fireAgencyProfileOptions: [],
    fireDispatchAreaOptions: [],
    fireSubdivision1Options: [],
    fireSubdivision2Options: [],
    fireSubdivision3Options: [],
    fireSubdivision4Options: [],
    fireSubdivision5Options: [],
    hairColorOptions: [],
    imageIndicatorOptions: [],
    itemColorOptions: [],
    personInvolvementTypeOptions: [],
    plateTypeOptions: [],
    policeAgencyProfileOptions: [],
    policeDispatchAreaOptions: [],
    policeSubdivision1Options: [],
    policeSubdivision2Options: [],
    policeSubdivision3Options: [],
    policeSubdivision4Options: [],
    policeSubdivision5Options: [],
    priorityOptions: [],
    purposeCodeOptions: [],
    raceOptions: [],
    radioChannelOptions: [],
    riskLevelOptions: [],
    scheduledEventFrequencyOptions: [],
    sexOptions: [],
    stateOptions: [],
    towCompanyOptions: [],
    towTypeOptions: [],
    vehicleInvolvementTypeOptions: [],
    drugCategoryOptions: [],
    firearmMakeOptions: [],
    itemCategoryOptions: [],
    measurementUnitsOptions: [],
    ncicFirearmCaliberOptions: [],
    firearmTypeOptions: [],
    itemIdentifierOptions: [],
    oriAliasOptions: [],
    towVehicleHoldOptions: [],
};

export function useEventQueueFilterOptions(): EventQueueFilterOptions {
    const { eventqueuefilteroptions: filterOptions } = useEventPanelOptions();

    const {
        subdivisionOptionsBootstrapResponse,
        cadDepartmentAgencyView,
        primaryEmsSubdivisionName,
        primaryPoliceSubdivisionName,
        primaryFireSubdivisionName,
        userAgencyTypeGlobalAttrId,
    } = useBootstrapState();

    const isQueueType = useIsAgencyTypeGlobalAttributeIdConsidered();

    const { multiAgencySubdivisions } = useEnabledFeatures();

    const primarySubdivisionNames: PrimarySubdivisionNames = useMemo(
        () => ({
            primaryPoliceSubdivisionName,
            primaryFireSubdivisionName,
            primaryEmsSubdivisionName,
        }),
        [primaryEmsSubdivisionName, primaryFireSubdivisionName, primaryPoliceSubdivisionName]
    );

    const { agencyId } = cadDepartmentAgencyView;

    const bootStrapView = multiAgencySubdivisions
        ? subdivisionOptionsBootstrapResponse?.agencyIdToSubdivisionOptionsBootstrapView?.[agencyId]
        : subdivisionOptionsBootstrapResponse?.combinedSubdivisionOptionsBootstrapView;

    const fireSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.FIRE,
        primarySubdivisionNames,
    });

    const policeSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.POLICE,
        primarySubdivisionNames,
    });

    const primarySubdivisionName = useMemo(() => {
        switch (userAgencyTypeGlobalAttrId) {
            case globalAttributes.agencyTypeGlobal.FIRE:
                return primarySubdivisionNames[
                    getPrimarySubdivisionNameKeyByAgencyType(globalAttributes.agencyTypeGlobal.FIRE)
                ];
            case globalAttributes.agencyTypeGlobal.EMS:
                return primarySubdivisionNames[
                    getPrimarySubdivisionNameKeyByAgencyType(globalAttributes.agencyTypeGlobal.EMS)
                ];
            default:
                return primarySubdivisionNames[
                    getPrimarySubdivisionNameKeyByAgencyType(
                        globalAttributes.agencyTypeGlobal.POLICE
                    )
                ];
        }
    }, [primarySubdivisionNames, userAgencyTypeGlobalAttrId]);

    const emsSubdivisionOptions = getPrimarySubdivisionOptions({
        subdivisionOptionsBootstrapView: bootStrapView,
        agencyTypeGlobalAttrId: globalAttributes.agencyTypeGlobal.EMS,
        primarySubdivisionNames,
    });

    const {
        alarmLevelOptions,
        callForServiceOptions: cfsOptions,
        eventLabelOptions,
        emsAgencyProfileOptions,
        fireAgencyProfileOptions,
        policeAgencyProfileOptions,
        emsDispatchAreaOptions,
        fireDispatchAreaOptions,
        policeDispatchAreaOptions,
        priorityOptions,
    } = useMemo(() => filterOptions, [filterOptions]);

    const additionalCfsOptions = useMemo(() => cfsOptions, [cfsOptions]);

    // only return relevant dispatch area options depending on queue class
    const dispatchAreaOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE) ? fireDispatchAreaOptions : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeDispatchAreaOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsDispatchAreaOptions : []),
        ],
        [fireDispatchAreaOptions, policeDispatchAreaOptions, emsDispatchAreaOptions, isQueueType]
    );

    // only return relevant agency options depending on queue class
    const agencyOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE)
                ? fireAgencyProfileOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeAgencyProfileOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsAgencyProfileOptions : []),
        ],
        [fireAgencyProfileOptions, policeAgencyProfileOptions, emsAgencyProfileOptions, isQueueType]
    );

    // only return relevant subdivision options depending on queue class
    const subdivisionOptions = useMemo(
        () => [
            ...(isQueueType(globalAttributes.agencyTypeGlobal.FIRE) ? fireSubdivisionOptions : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.POLICE)
                ? policeSubdivisionOptions
                : []),
            ...(isQueueType(globalAttributes.agencyTypeGlobal.EMS) ? emsSubdivisionOptions : []),
        ],
        [fireSubdivisionOptions, policeSubdivisionOptions, emsSubdivisionOptions, isQueueType]
    );

    return {
        additionalCfsOptions: additionalCfsOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.ADDITIONAL_CFS.name,
        })),
        cfsOptions: cfsOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.CFS_NUMBER_AND_TEXT.name,
        })),
        priorityOptions: priorityOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.PRIORITY.name,
        })),
        alarmLevelOptions: alarmLevelOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.ALARM_LEVEL.name,
        })),
        eventLabelOptions: eventLabelOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.EVENT_LABELS.name,
        })),
        dispatchAreaOptions: dispatchAreaOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.DISPATCH_AREA.name,
        })),
        agencyOptions: agencyOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.AGENCY.name,
        })),
        eventPrimarySubdivisionOptions: subdivisionOptions.map((option) => ({
            ...option,
            columnDefinition: ColumnDefinitionEnum.BEAT.name,
        })),
        primarySubdivisionName,
    };
}
